import { environnement } from "configs/environnement";
import api from "./api";
import {
  IContributionResponse,
  ISaveContribution,
  IUpdateContribution,
} from "models/church.types";

const BASE_URL = `${environnement.baseURL}/contributions`;

export const ContributionService = {
  /**
   * Récupérer la liste des contributions (via Elasticsearch)
   */
  getAll: async (page: number, size: number) => {
    const response = await api.get<IContributionResponse[]>(
      `${BASE_URL}/_search`,
      {
        params: {
          page: page,
          size: size,
          sort: "id,desc",
        },
      },
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
    };
  },

    /**
   * Récupérer les contributions des membres d'un département spécifique
   */
  getByDepartment: async (
    departmentId: number | string,
    page: number,
    size: number,
  ) => {
    const response = await api.get<IContributionResponse[]>(
      `${BASE_URL}/department/${departmentId}`,
      {
        params: {
          page: page,
          size: size,
          sort: "id,desc",
        },
      },
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
    };
  },

  /**
   * Créer une nouvelle contribution
   */
  create: async (
    contribution: ISaveContribution,
  ): Promise<IContributionResponse> => {
    const response = await api.post<IContributionResponse>(
      `${BASE_URL}`,
      contribution,
    );
    return response.data;
  },

  /**
   * Mettre à jour une contribution existante
   */
  update: async (
    contribution: IUpdateContribution,
  ): Promise<IContributionResponse> => {
    const response = await api.put<IContributionResponse>(
      `${BASE_URL}`,
      contribution,
    );
    return response.data;
  },

  /**
   * Récupérer une contribution par son ID
   */
  getById: async (id: number): Promise<IContributionResponse> => {
    const response = await api.get<IContributionResponse>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Supprimer une contribution
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Rechercher des contributions (Elasticsearch)
   * Permet de chercher par membre, observation, etc.
   */
  search: async (query: string, page: number, size: number) => {
    const response = await api.get<IContributionResponse[]>(
      `${BASE_URL}/_search`,
      {
        params: {
          query: query,
          page: page,
          size: size,
          sort: "id,desc",
        },
      },
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
    };
  },

  /**
   * Récupérer l'historique des contributions d'un membre
   */
  getByMember: async (memberId: number, page: number, size: number) => {
    const response = await api.get<IContributionResponse[]>(
      `${BASE_URL}/member/${memberId}`,
      {
        params: {
          page: page,
          size: size,
          sort: "id,desc",
        },
      },
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
    };
  },

  /**
   * Récupérer les contributions par type spécifique
   */
  getByType: async (typeId: number | string, page: number, size: number) => {
    const response = await api.get<IContributionResponse[]>(
      `${BASE_URL}/type/${typeId}`,
      {
        params: {
          page: page,
          size: size,
          sort: "id,desc",
        },
      },
    );
    return {
      data: response.data,
      totalCount: parseInt(response.headers["x-total-count"] || "0"),
    };
  },

  /**
   * Récupérer le montant total des contributions sur une période,
   * avec option de filtrage par type.
   */
  getSummary: async (
    startDate?: string,
    endDate?: string,
    typeId?: number | string,
  ): Promise<number> => {
    const response = await api.get<number>(`${BASE_URL}/summary`, {
      params: {
        startDate,
        endDate,
        typeId,
      },
    });
    return response.data;
  },
};